import React, { useCallback, useEffect, useMemo, useRef, type ComponentType } from 'react';
import CacheRegistry from '@atlassian/jira-cache/src/ui/index.tsx';
import {
	projectTypeToCanonicalId,
	toProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { AsyncPendoAnalyticsWrapper } from '@atlassian/jira-common-pendo-analytics/src/async.tsx';
import { SafeConversationAssistantPanel } from '@atlassian/jira-conversation-assistant/src/index.tsx';
import { AsyncDeferEditionEPCheckWrapper } from '@atlassian/jira-defer-edition-modal/async.tsx';
import { jiraDeferEditionModalEntryPoint } from '@atlassian/jira-defer-edition-modal/entrypoint.tsx';
import { endOfTrialExtensionEntryPoint } from '@atlassian/jira-end-of-trial-extension/entrypoint.tsx';
import { AsyncEndOfTrialMessageSharedWrapper } from '@atlassian/jira-end-of-trial-message-shared-global-component/src/async.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import EventDelayController from '@atlassian/jira-event-delay-controller/src/index.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { LoadingBarUIController } from '@atlassian/jira-global-loading-bar/src/controllers/loading-bar-ui-controller/LoadingBarUIController.tsx';
import { SafeHelpPanel } from '@atlassian/jira-help-panel-right-sidebar/src/index.tsx';
import { SafeEmbeddedConfluencePanel } from '@atlassian/jira-issue-create-confluence-content/src/ui/embedded-right-sidebar/index.tsx';
import { ImperativeAPI } from '@atlassian/jira-legacy-imperative-api/src/index.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { PageDwellAnalyticsListener } from '@atlassian/jira-page-dwell-analytics-listener/src/index.tsx';
import { MatchedRouteListener } from '@atlassian/jira-platform-route-analytics/src/ui/matched-route-listener/index.tsx';
import { PreviousRouterStateListener } from '@atlassian/jira-platform-route-analytics/src/ui/previous-router-state-listener/index.tsx';
import QueryStringNotifier from '@atlassian/jira-query-string-notifier/src/index.tsx';
import { AsyncRecommendPremiumToStandardModalWrapper } from '@atlassian/jira-recommend-premium-to-standard-modal/src/async.tsx';
import { createLayout, type Layout } from '@atlassian/jira-route-layout/src/GlobalComponent.tsx';
import { rovoVideoModalEntryPoint } from '@atlassian/jira-rovo-video-modal/entrypoint.tsx';
import ProjectKeyRedirect from '@atlassian/jira-spa/src/services/project-key-redirect/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { AsyncTrialSprintBoardModalEntryPoint } from '@atlassian/jira-trial-sprint-board/src/ui/modal/async.tsx';
import { AsyncUpflowIframeComponent } from '@atlassian/jira-upflow-iframe-plugin/src/async.tsx';
import { JiraHeritageLoader } from '@atlassian/jira-wrm/src/jira-heritage-loader/index.tsx';
import { lazy, lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { isJiraSoftware } from '@atlassian/jira-rovo-video-modal/src/utils.tsx';
import { getRovoVideoOnboardingHash } from '@atlassian/jira-rovo-video-modal/src/services/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useCoordinationClient } from '@atlassian/jira-rovo-video-modal/src/services/coordination-client.tsx';
import { useRovoEntitlementResource } from '@atlassian/jira-router-resources-rovo-entitlement/src/services/index.tsx';
import { BreadcrumbAdapter } from './ui/breadcrumbs/BreadcrumbAdapter.tsx';
import { LoadingPhaseManager } from './ui/loading-phase-manager/index.tsx';
import { TrackClicksBeforeTTI } from './ui/track-clicks-before-tti/index.tsx';
import { Integrations } from './ui/integrations/index.tsx';

// +owner:pdhanuka
// eslint-disable-next-line jira/ff/inline-usage
const isPendoIntegrationEnabled = () => fg('jira-frontend-pendo-analytics_s6sj5');
// -owner:pdhanuka

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const Global = lazy(
	() => import(/* webpackChunkName: "async-global-global-components" */ './ui/global'),
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const Navigation = lazyAfterPaint(
	() => import(/* webpackChunkName: "async-global-navigation-components" */ './ui/navigation'),
	{
		ssr: false,
	},
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const Trackers = lazyAfterPaint(
	() => import(/* webpackChunkName: "async-global-trackers-components" */ './ui/trackers'),
	{
		ssr: false,
	},
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const RemoteActions = lazyAfterPaint(
	() => import(/* webpackChunkName: "async-remote-actions" */ './ui/remote-actions'),
	{
		ssr: false,
	},
);

// No need for a whole chunk for a single component
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AfterPaintIssuePreEval = lazyAfterPaint(
	/* eslint-disable-next-line jira/import/webpack-magic-comment-entries */
	() => import('./ui/after-paint-issue-preeval/AfterPaintIssuePreEval.tsx'),
	{
		ssr: false,
	},
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AsyncBrowserStorageControlsInit = lazy(() =>
	import(
		/* webpackChunkName: "async-browser-storage-controls-init" */ './ui/browser-storage-controls-init'
	).then(({ BrowserStorageControlsInit }) => BrowserStorageControlsInit),
);

const runtimeProps = {};
const entryPointParams = {};

export const DeferRovoVideoPlayerEntryPoint = () => {
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		rovoVideoModalEntryPoint,
		entryPointParams,
	);
	const { atlassianAccountId, cloudId, isAnonymous } = useTenantContext();
	const { loading: loadingProject, data: project } = useProject();
	const projectType = toProjectType(project?.type);
	const { start: messageStarted } = useCoordinationClient();
	const rovoEntitlement = useRovoEntitlementResource();
	const isRovoLLMEnabled = !!rovoEntitlement?.data?.isRovoLLMEnabled;

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		const shouldShowRovoVideoModal = async () => {
			const hasSeenRovoOnboarding = await getRovoVideoOnboardingHash(atlassianAccountId);

			if (
				// eslint-disable-next-line jira/ff/no-preconditioning
				!loadingProject &&
				isJiraSoftware(projectType) &&
				fg('rovo_video_player_gate') &&
				!hasSeenRovoOnboarding &&
				!isAnonymous &&
				isRovoLLMEnabled
			) {
				const hasMessageStarted = await messageStarted();

				if (hasMessageStarted) {
					entryPointActions.load();
				}
			}
		};
		shouldShowRovoVideoModal();
	}, [
		atlassianAccountId,
		cloudId,
		entryPointActions,
		isAnonymous,
		loadingProject,
		messageStarted,
		projectType,
		isRovoLLMEnabled,
	]);

	return (
		<JiraEntryPointContainer
			id="growth.rovoVideoPlayer"
			packageName="rovo-video-modal"
			entryPointReferenceSubject={entryPointReferenceSubject}
			fallback={null}
			runtimeProps={runtimeProps}
		/>
	);
};

export const DeferTrialSprintBoardModalEntryPoint = () => {
	const { data: project } = useProject();
	const projectId = String(project?.id);
	return <AsyncTrialSprintBoardModalEntryPoint currentProjectId={projectId} />;
};

export const DeferEditionModalEntryPoint = () => {
	const cloudID = useCloudId();
	const { data: project } = useProject();
	const projectType = toProjectType(project?.type) || null;
	const productKey = projectTypeToCanonicalId(projectType);

	const deferEditionModalEntryPointParams = useMemo(
		() => ({
			cloudId: cloudID,
			productKey,
		}),
		[cloudID, productKey],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		jiraDeferEditionModalEntryPoint,
		deferEditionModalEntryPointParams,
	);

	const onComplete = useCallback(() => {
		entryPointActions.load();
	}, [entryPointActions]);

	return (
		<AsyncDeferEditionEPCheckWrapper onComplete={onComplete}>
			<JiraEntryPointContainer
				id="growth.jiraDeferEditionModal"
				packageName="jira-defer-edition-modal"
				entryPointReferenceSubject={entryPointReferenceSubject}
				fallback={null}
				runtimeProps={runtimeProps}
			/>
		</AsyncDeferEditionEPCheckWrapper>
	);
};

const EndOfTrialModalEntryPoint = () => {
	const hasLoaded = useRef(false);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		endOfTrialExtensionEntryPoint,
		{},
	);

	if (!hasLoaded.current) {
		hasLoaded.current = true;
		entryPointActions.load();
	}

	return (
		<JiraEntryPointContainer
			id="growth.endOfTrialExtension"
			packageName="jira-end-of-trial-extension"
			entryPointReferenceSubject={entryPointReferenceSubject}
			fallback={null}
			runtimeProps={runtimeProps}
		/>
	);
};

const DeferEditionModal = componentWithFG(
	'cross-flow-defer-edition-communication-gate',
	DeferEditionModalEntryPoint,
	() => null,
);

const DeferRovoVideoModal = componentWithFG(
	'rovo_video_player_gate',
	DeferRovoVideoPlayerEntryPoint,
	() => null,
);

const DeferTrialSprintBoardModal = componentWithFG(
	'jsw-trial-sprint-board-modal-gate-v2',
	DeferTrialSprintBoardModalEntryPoint,
	() => null,
);

const AsyncRecommendPremiumToStandardModal = componentWithFG(
	'recommend_standard_in_jira_eot_shared_layout_',
	AsyncRecommendPremiumToStandardModalWrapper,
	() => null,
);

const ConditionalEndOfTrialExtensionModal = componentWithFG(
	'end_of_trial_extension_kill_switch',
	EndOfTrialModalEntryPoint,
	() => null,
);

DeferEditionModal.displayName = 'DeferEditionModal';
DeferRovoVideoModal.displayName = 'DeferRovoVideoModal';
DeferTrialSprintBoardModal.displayName = 'DeferTrialSprintBoardModal';
AsyncRecommendPremiumToStandardModal.displayName = 'AsyncRecommendPremiumToStandardModal';
ConditionalEndOfTrialExtensionModal.displayName = 'ConditionalEndOfTrialExtensionModal';

const sharedGlobalComponents: ComponentType<{}>[] = [
	...(__SERVER__
		? []
		: [
				MatchedRouteListener,
				PreviousRouterStateListener,
				LoadingPhaseManager,
				LoadingBarUIController,
				CacheRegistry,
				BreadcrumbAdapter,
				QueryStringNotifier,
				PageDwellAnalyticsListener,
				ImperativeAPI,
				TrackClicksBeforeTTI,
				ProjectKeyRedirect,
				AsyncUpflowIframeComponent,
				JiraHeritageLoader,
				AsyncBrowserStorageControlsInit,
				...(isPendoIntegrationEnabled() ? [AsyncPendoAnalyticsWrapper] : []),
				EventDelayController,
				RemoteActions,
				DeferEditionModal,
				DeferRovoVideoModal,
				DeferTrialSprintBoardModal,
				AsyncEndOfTrialMessageSharedWrapper,
				AsyncRecommendPremiumToStandardModal,
				ConditionalEndOfTrialExtensionModal,
				// Integrations - Issue View Modal, Create Project Drawer & Create dashboard modal
				Integrations,
			]),
	Global,
	Navigation,
	Trackers,
	AfterPaintIssuePreEval,
];

const SafeEmbeddedConfluencePanelWithExperiments = () => {
	const [expConfig] = UNSAFE_noExposureExp('jira_issue_view_side_by_side_modeless_ep_exp');
	return expConfig.get('cohort', 'not-enrolled') === 'experiment' ? (
		<SafeEmbeddedConfluencePanel />
	) : null;
};

export const sharedLayout: Layout = createLayout({
	rightSidebars: [
		SafeHelpPanel,
		SafeConversationAssistantPanel,
		SafeEmbeddedConfluencePanelWithExperiments,
	],
	globalComponents: sharedGlobalComponents,
});
